import type { AdNode } from "../../core/AdNode";
import type { GptPlatformPlugin } from "./GptPlatformPlugin";
import { getFandomContext } from "../../helpers/getFandomContext";
import { getTop1kWikiString } from "../../helpers/getTop1kWikiString";
import type { GptWrapper } from "../../core/GptWrapper";
import { PerformanceMarkName } from "common-types";
import type { SlotName } from "../../config/platforms/types";

/**
 * Unified Platform specific Gpt Plugin. This will be used for all UCP renders to define
 * custom Gpt data like AdUnitPaths.
 */
export class UcpGptPlatformPlugin implements GptPlatformPlugin {
  // TODO: This might need to be configured based on some conditions when we begin supporting video
  // or other types of slots. But for now, all display slots are using wka1b.
  private readonly SERVER_PREFIX = "wka1b";

  constructor(private readonly gptWrapper: GptWrapper) {}

  /**
   * Builds the ad unit path for the provided node for UCP platforms. The ad unit path currently
   * uses the following format:
   * > /${gamId}/${this.SERVER_PREFIX}.${slotConfig.group}/${adNode.name}/${fandomHost.device}/${skin}-${product}-${s2:pageType}/${top1kString}-${s0:vertical}
   *
   * That for example, will create the following path for incontent leaderboard 2 on desktop for a top1K wiki.
   * > /5441/wka1b.ILB/incontent_leaderboard_2/desktop/ucp_desktop-fandom-wv-article/_top1k_wiki-ent
   *
   * @param node The node to create the ad unit path
   * @returns The generated ad unit path string
   */
  buildAdUnitPathForNode(node: AdNode): string {
    const fandomContext = getFandomContext();

    const gamId = this.gptWrapper.getGamId();
    const { group } = node.getSlotConfig();
    const name = node.getSlotName();
    const top1kString = getTop1kWikiString(fandomContext);
    const [product = "fandom"] = this.gptWrapper.getTargeting("product");
    const [s2 = "article"] = this.gptWrapper.getTargeting("s2");
    const [s0 = "all"] = this.gptWrapper.getTargeting("s0");
    const [skin = "ucp_desktop"] = this.gptWrapper.getTargeting("skin");
    const [device = "desktop"] = this.gptWrapper.getTargeting("device");

    return `/${gamId}/${this.SERVER_PREFIX}.${group}/${name}/${device}/${skin}-${product}-${s2}/${top1kString}-${s0}`;
  }

  /**
   * Sets up performance mark collection for the first ad call in UCP (top_leaderboard).
   *
   * @param gpt The GptWrapper instance
   */
  setupPerformanceMarks(gpt: GptWrapper): void {
    gpt.addToCommandQueue(() => {
      // slotRequested - Slot has been requested
      this.registerEventPerformanceMarkCallback(
        gpt,
        "slotRequested",
        "top_leaderboard",
      );
      // slotResponseReceived - Ad response has been received
      this.registerEventPerformanceMarkCallback(
        gpt,
        "slotResponseReceived",
        "top_leaderboard",
      );
      // slotRenderEnded - Slot has finished rendering
      this.registerEventPerformanceMarkCallback(
        gpt,
        "slotRenderEnded",
        "top_leaderboard",
      );
      // slotOnload - Creative iframe load event has fired
      this.registerEventPerformanceMarkCallback(
        gpt,
        "slotOnload",
        "top_leaderboard",
      );
    });
  }

  private registerEventPerformanceMarkCallback<
    T extends keyof googletag.events.EventTypeMap,
  >(gpt: GptWrapper, event: T, slotName: SlotName) {
    gpt.addEventListener(event, ({ slot }: googletag.events.Event) => {
      const renderCount = slot.getTargeting("rv");
      const isFirstCall = renderCount.length > 0 && renderCount[0] === "1";

      if (isFirstCall && slot.getSlotElementId() === slotName) {
        const initialMarkName = this.getInitialSlotPerformanceMarkName(event);

        if (initialMarkName) {
          performance.mark(initialMarkName.toString(), {
            detail: {
              slotId: slotName,
            },
          });
        }
      }
    });
  }

  private getInitialSlotPerformanceMarkName<
    T extends keyof googletag.events.EventTypeMap,
  >(event: T) {
    switch (event) {
      case "slotRequested":
        return PerformanceMarkName.AE_GPT_SLOT_REQUESTED_INITIAL;
      case "slotResponseReceived":
        return PerformanceMarkName.AE_GPT_SLOT_RESPONSE_RECEIVED_INITIAL;
      case "slotRenderEnded":
        return PerformanceMarkName.AE_GPT_SLOT_RENDERED_ENDED_INITIAL;
      case "slotOnload":
        return PerformanceMarkName.AE_GPT_SLOT_SLOT_ONLOAD_INITIAL;
      default:
        break;
    }
  }
}
